import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  createChannel,
  createSession,
  getBitrixUsers,
  startSession,
} from '../api.js';

import '../connection-wizard.css';


const CONNECTION_TYPES = [
  {
    key: 'telegram_personal',
    messenger: 'Telegram',
    mode: 'Личный аккаунт',
    icon: '/brand/messengers/telegram-personal.svg',
    personal: true,
    messengerType: 'telegram',
    description:
      'Рабочий Telegram-аккаунт по номеру телефона.',
  },
  {
    key: 'telegram_bot',
    messenger: 'Telegram',
    mode: 'Бот',
    icon: '/brand/messengers/telegram-bot.svg',
    personal: false,
    description:
      'Официальный Telegram Bot API по токену бота.',
  },
  {
    key: 'max_personal',
    messenger: 'MAX',
    mode: 'Личный аккаунт',
    icon: '/brand/messengers/max-personal.svg',
    personal: true,
    messengerType: 'max',
    description:
      'Рабочий личный аккаунт MAX.',
  },
  {
    key: 'max_bot',
    messenger: 'MAX',
    mode: 'Бот',
    icon: '/brand/messengers/max-bot.svg',
    personal: false,
    description:
      'Официальный MAX Bot по токену.',
  },
  {
    key: 'whatsapp_personal',
    messenger: 'WhatsApp',
    mode: 'Личный аккаунт',
    icon: '/brand/messengers/whatsapp-personal.svg',
    personal: true,
    messengerType: 'whatsapp',
    description:
      'Рабочий WhatsApp-аккаунт с авторизацией по QR.',
  },
  {
    key: 'whatsapp_business',
    messenger: 'WhatsApp',
    mode: 'Business API',
    icon: '/brand/messengers/whatsapp-personal.svg',
    personal: false,
    description:
      'WhatsApp Business API через Meta.',
  },
];


const EMPTY_DETAILS = {
  name: '',
  phone_number: '',
  bot_token: '',
  phone_number_id: '',
  access_token: '',
};


function employeeName(user) {
  const name = [
    user?.name,
    user?.last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    name
    || `Сотрудник ${user?.id || ''}`
  );
}


function connectionDefaultName(type) {
  if (!type) {
    return 'Канал Связки';
  }

  return `${type.messenger} — ${type.mode}`;
}


function detailsReady(type, details) {
  if (!type) {
    return false;
  }

  if (type.key === 'telegram_personal') {
    return Boolean(
      details.phone_number.trim()
    );
  }

  if (type.key === 'whatsapp_personal') {
    return Boolean(
      details.phone_number.trim()
    );
  }

  if (type.key === 'max_personal') {
    return true;
  }

  if (
    type.key === 'telegram_bot'
    || type.key === 'max_bot'
  ) {
    return Boolean(
      details.bot_token.trim()
    );
  }

  if (
    type.key === 'whatsapp_business'
  ) {
    return Boolean(
      details.phone_number_id.trim()
      && details.access_token.trim()
    );
  }

  return false;
}


function persistPendingPersonal(
  pending
) {
  try {
    window.sessionStorage.setItem(
      'svyazka:pending-personal-auth',
      JSON.stringify(pending),
    );
  } catch {
    // The event handoff below remains available.
  }
}


function openPersonalAuthorization(
  pending
) {
  persistPendingPersonal(
    pending
  );

  window.dispatchEvent(
    new CustomEvent(
      'svyazka:workspace-tab',
      {
        detail: 'personal',
      },
    ),
  );

  window.setTimeout(
    () => {
      window.dispatchEvent(
        new CustomEvent(
          'svyazka:personal-auth-open',
          {
            detail: pending,
          },
        ),
      );
    },
    0,
  );
}


function ConnectionWizardInner() {
  const [step, setStep] =
    useState(1);

  const [selectedKey, setSelectedKey] =
    useState('');

  const [details, setDetails] =
    useState(EMPTY_DETAILS);

  const [users, setUsers] =
    useState([]);

  const [
    selectedOperators,
    setSelectedOperators,
  ] = useState([]);

  const [usersLoading, setUsersLoading] =
    useState(true);

  const [usersError, setUsersError] =
    useState('');

  const [submitting, setSubmitting] =
    useState(false);

  const [submitError, setSubmitError] =
    useState('');

  const [result, setResult] =
    useState(null);


  const type = useMemo(
    () => CONNECTION_TYPES.find(
      (item) => (
        item.key === selectedKey
      )
    ) || null,
    [selectedKey],
  );


  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError('');

      try {
        const response =
          await getBitrixUsers();

        const items = Array.isArray(
          response?.items
        )
          ? response.items
          : [];

        if (!cancelled) {
          setUsers(items);
        }
      } catch (error) {
        if (!cancelled) {
          setUsersError(
            error?.message
            || (
              'Не удалось получить '
              + 'сотрудников Битрикс24'
            )
          );
        }
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, []);


  const reset = () => {
    setStep(1);
    setSelectedKey('');
    setDetails(EMPTY_DETAILS);
    setSelectedOperators([]);
    setSubmitError('');
    setResult(null);
    setSubmitting(false);
  };


  const chooseType = (
    key
  ) => {
    setSelectedKey(key);
    setDetails(EMPTY_DETAILS);
    setSelectedOperators([]);
    setSubmitError('');
    setResult(null);
    setStep(2);
  };


  const toggleOperator = (
    id
  ) => {
    const value = String(id);

    setSelectedOperators(
      (current) => (
        current.includes(value)
          ? current.filter(
              (item) => (
                item !== value
              )
            )
          : [
              ...current,
              value,
            ]
      ),
    );
  };


  const submit = async () => {
    if (
      !type
      || selectedOperators.length === 0
    ) {
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setResult(null);

    try {
      if (type.personal) {
        const response =
          await createSession({
            messenger_type:
              type.messengerType,
            phone_number:
              type.key === 'max_personal'
                ? ''
                : details.phone_number.trim(),
            operator_external_ids:
              selectedOperators,
          });

        const pending = {
          id: response.session_id,
          channel_id:
            response.channel_id,
          messenger_type:
            type.messengerType,
          phone_number:
            details.phone_number.trim(),
          auth_state: 'authorizing',
        };

        persistPendingPersonal(
          pending
        );

        try {
          await startSession(
            response.session_id
          );
        } catch (startError) {
          setResult({
            kind: 'personal-created',
            pending,
            lineId:
              response.bitrix_line_id
              || null,
            message:
              (
                'Канал и Открытая линия созданы, '
                + 'но авторизация не запустилась '
                + 'автоматически.'
              ),
          });

          setSubmitError(
            startError?.message
            || 'Не удалось запустить авторизацию'
          );

          openPersonalAuthorization(
            pending
          );

          setStep(4);
          return;
        }

        setResult({
          kind: 'personal',
          pending,
          lineId:
            response.bitrix_line_id
            || null,
          message:
            (
              'Открытая линия создана. '
              + 'Продолжите авторизацию аккаунта.'
            ),
        });

        setStep(4);

        openPersonalAuthorization(
          pending
        );

        return;
      }

      const name = (
        details.name.trim()
        || connectionDefaultName(type)
      );

      let credentials = {};

      if (
        type.key === 'telegram_bot'
        || type.key === 'max_bot'
      ) {
        credentials = {
          bot_token:
            details.bot_token.trim(),
        };
      }

      if (
        type.key === 'whatsapp_business'
      ) {
        credentials = {
          phone_number_id:
            details.phone_number_id.trim(),
          access_token:
            details.access_token.trim(),
        };
      }

      const channel =
        await createChannel({
          name,
          channel_type:
            type.key,
          credentials,
          operator_external_ids:
            selectedOperators,
        });

      setResult({
        kind: 'channel',
        channelId: channel.id,
        lineId:
          channel.bitrix_line_id
          || null,
        message:
          (
            'Канал создан и подключён '
            + 'к собственной Открытой линии.'
          ),
      });

      setStep(4);

      window.dispatchEvent(
        new CustomEvent(
          'svyazka:workspace-tab',
          {
            detail: 'bots',
          },
        ),
      );

      window.setTimeout(
        () => {
          window.dispatchEvent(
            new CustomEvent(
              'svyazka:channels-refresh'
            ),
          );
        },
        0,
      );
    } catch (error) {
      setSubmitError(
        error?.message
        || 'Не удалось подключить канал'
      );
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <section className="sv-wizard">
      <div className="sv-wizard-head">
        <div>
          <div className="sv-wizard-eyebrow">
            НОВОЕ ПОДКЛЮЧЕНИЕ
          </div>

          <h2>
            Подключить канал
          </h2>

          <p>
            Выберите мессенджер,
            укажите данные аккаунта
            и сотрудников. Связка сама
            создаст отдельную Открытую
            линию в Битрикс24.
          </p>
        </div>

        <div className="sv-wizard-steps">
          {[1, 2, 3, 4].map(
            (number) => (
              <span
                key={number}
                className={
                  number === step
                    ? 'active'
                    : (
                      number < step
                        ? 'done'
                        : ''
                    )
                }
              >
                {number}
              </span>
            ),
          )}
        </div>
      </div>

      {step === 1 && (
        <div className="sv-wizard-types">
          {CONNECTION_TYPES.map(
            (item) => (
              <button
                type="button"
                key={item.key}
                className="sv-wizard-type"
                onClick={
                  () => chooseType(
                    item.key
                  )
                }
              >
                <img
                  src={item.icon}
                  alt=""
                />

                <div>
                  <strong>
                    {item.messenger}
                  </strong>

                  <span>
                    {item.mode}
                  </span>

                  <small>
                    {item.description}
                  </small>
                </div>
              </button>
            ),
          )}
        </div>
      )}

      {step === 2 && type && (
        <div className="sv-wizard-panel">
          <div className="sv-wizard-selected">
            <img
              src={type.icon}
              alt=""
            />

            <div>
              <strong>
                {type.messenger}
              </strong>

              <span>
                {type.mode}
              </span>
            </div>
          </div>

          {!type.personal && (
            <label className="sv-wizard-field">
              <span>
                Название канала
              </span>

              <input
                type="text"
                value={details.name}
                placeholder={
                  connectionDefaultName(
                    type
                  )
                }
                onChange={
                  (event) => setDetails(
                    {
                      ...details,
                      name:
                        event.target.value,
                    }
                  )
                }
              />
            </label>
          )}

          {(
            type.key ===
              'telegram_personal'
            || type.key ===
              'whatsapp_personal'
          ) && (
            <label className="sv-wizard-field">
              <span>
                Номер телефона
              </span>

              <input
                type="tel"
                value={
                  details.phone_number
                }
                placeholder="+79001234567"
                autoComplete="tel"
                onChange={
                  (event) => setDetails(
                    {
                      ...details,
                      phone_number:
                        event.target.value,
                    }
                  )
                }
              />
            </label>
          )}

          {type.key === 'max_personal' && (
            <div className="sv-wizard-hint">
              Номер телефона MAX будет
              запрошен на этапе авторизации.
            </div>
          )}

          {(
            type.key === 'telegram_bot'
            || type.key === 'max_bot'
          ) && (
            <label className="sv-wizard-field">
              <span>
                Токен бота
              </span>

              <input
                type="password"
                value={
                  details.bot_token
                }
                autoComplete="off"
                placeholder="Введите токен"
                onChange={
                  (event) => setDetails(
                    {
                      ...details,
                      bot_token:
                        event.target.value,
                    }
                  )
                }
              />
            </label>
          )}

          {(
            type.key ===
              'whatsapp_business'
          ) && (
            <>
              <label className="sv-wizard-field">
                <span>
                  Идентификатор номера
                </span>

                <input
                  type="text"
                  value={
                    details.phone_number_id
                  }
                  autoComplete="off"
                  onChange={
                    (event) => setDetails(
                      {
                        ...details,
                        phone_number_id:
                          event.target.value,
                      }
                    )
                  }
                />
              </label>

              <label className="sv-wizard-field">
                <span>
                  Токен доступа
                </span>

                <input
                  type="password"
                  value={
                    details.access_token
                  }
                  autoComplete="off"
                  onChange={
                    (event) => setDetails(
                      {
                        ...details,
                        access_token:
                          event.target.value,
                      }
                    )
                  }
                />
              </label>
            </>
          )}

          <div className="sv-wizard-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setStep(1)}
            >
              Назад
            </button>

            <button
              type="button"
              className="btn-primary"
              disabled={
                !detailsReady(
                  type,
                  details
                )
              }
              onClick={() => setStep(3)}
            >
              Далее
            </button>
          </div>
        </div>
      )}

      {step === 3 && type && (
        <div className="sv-wizard-panel">
          <div>
            <h3>
              Кто будет отвечать клиентам
            </h3>

            <p className="sv-wizard-copy">
              Выберите сотрудников.
              Они попадут в очередь
              новой Открытой линии.
            </p>
          </div>

          {usersLoading ? (
            <div className="sv-wizard-hint">
              Получаем сотрудников Битрикс24…
            </div>
          ) : usersError ? (
            <div className="sv-wizard-error">
              {usersError}
            </div>
          ) : users.length === 0 ? (
            <div className="sv-wizard-error">
              В Битрикс24 не найдено
              активных сотрудников.
            </div>
          ) : (
            <div className="sv-operator-grid">
              {users.map(
                (user) => {
                  const selected =
                    selectedOperators.includes(
                      String(user.id)
                    );

                  return (
                    <label
                      className={
                        selected
                          ? (
                            'sv-operator-card '
                            + 'selected'
                          )
                          : 'sv-operator-card'
                      }
                      key={user.id}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={
                          () => toggleOperator(
                            user.id
                          )
                        }
                      />

                      <div>
                        <strong>
                          {employeeName(user)}
                        </strong>

                        <span>
                          {user.work_position
                            || (
                              'Сотрудник '
                              + 'Битрикс24'
                            )}
                        </span>
                      </div>
                    </label>
                  );
                },
              )}
            </div>
          )}

          {submitError && (
            <div className="sv-wizard-error">
              {submitError}
            </div>
          )}

          <div className="sv-wizard-actions">
            <button
              type="button"
              className="btn-secondary"
              disabled={submitting}
              onClick={() => setStep(2)}
            >
              Назад
            </button>

            <button
              type="button"
              className="btn-primary"
              disabled={
                submitting
                || usersLoading
                || Boolean(usersError)
                || (
                  selectedOperators.length
                  === 0
                )
              }
              onClick={submit}
            >
              {submitting
                ? 'Подключаем…'
                : 'Подключить'}
            </button>
          </div>
        </div>
      )}

      {step === 4 && result && (
        <div className="sv-wizard-result">
          <div className="sv-wizard-result-icon">
            ✓
          </div>

          <div>
            <h3>
              {result.kind === 'channel'
                ? 'Канал готов'
                : 'Открытая линия готова'}
            </h3>

            <p>
              {result.message}
            </p>

            {result.lineId && (
              <small>
                Открытая линия создана
                и закреплена за этим каналом.
              </small>
            )}
          </div>

          <div className="sv-wizard-result-actions">
            {result.pending && (
              <button
                type="button"
                className="btn-primary"
                onClick={
                  () => openPersonalAuthorization(
                    result.pending
                  )
                }
              >
                Продолжить авторизацию
              </button>
            )}

            <button
              type="button"
              className="btn-secondary"
              onClick={reset}
            >
              Подключить ещё канал
            </button>
          </div>

          {submitError && (
            <div className="sv-wizard-error">
              {submitError}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


import {
  useEffect as usePaymentEffect,
  useState as usePaymentState,
} from 'react';

import {
  getBilling,
  getSubscription,
} from '../api.js';

import PaymentChoice from './PaymentChoice.jsx';

export default function ConnectionWizard() {
  const [access, setAccess] = usePaymentState({
    loading: true,
    permitted: false,
    paid: false,
    used: false,
    error: false,
  });

  usePaymentEffect(() => {
    let active = true;

    async function refresh() {
      try {
        const [billing, usage] = await Promise.all([
          getBilling(),
          getSubscription(),
        ]);

        if (!active) return;

        const legacy =
          billing?.status === 'active' &&
          billing?.is_legacy_subscription === true;

        const paidUntil = billing?.paid_until
          ? Date.parse(billing.paid_until)
          : 0;

        const paid =
          billing?.status === 'active' &&
          (legacy || paidUntil > Date.now());

        const remaining = Number(
          usage?.remaining_channels ?? 0
        );

        setAccess({
          loading: false,
          permitted: paid && remaining > 0,
          paid,
          used: paid && remaining <= 0,
          error: false,
        });
      } catch {
        if (active) {
          setAccess({
            loading: false,
            permitted: false,
            paid: false,
            used: false,
            error: true,
          });
        }
      }
    }

    refresh();

    const interval = setInterval(refresh, 5000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  if (access.loading) {
    return (
      <section className="mf-billing">
        <p>Проверяем подписку…</p>
      </section>
    );
  }

  if (access.error) {
    return (
      <section className="mf-billing">
        <h2>Не удалось проверить подписку</h2>
        <p>
          Обновите страницу. Подключение будет доступно,
          когда проверка завершится.
        </p>
      </section>
    );
  }

  if (access.used) {
    return (
      <section className="mf-billing">
        <h2>Все оплаченные каналы используются</h2>
        <p>
          Подписка активна. Вы можете управлять уже
          созданными каналами в разделе «Каналы».
        </p>
        <PaymentChoice />
      </section>
    );
  }

  if (!access.permitted) {
    return (
      <section className="mf-billing">
        <h2>Оплатите подключение канала</h2>
        <p>
          Для подключения мессенджера необходима
          активная подписка. После оплаты подключение
          станет доступно автоматически.
        </p>
        <PaymentChoice />
      </section>
    );
  }

  return <ConnectionWizardInner />;
}
